/**
 * Callback for submitting a search request, debounced by the filter box to reduce service calls.
 * @callback searchRequest
 * @param {string} simple query string to search for
 */
/**
 * Typical PrintOS list count structure containing information about the total and limit query amounts
 * @typedef {object} listCounts
 * @property {number} total indicates the collection total
 * @property {number} limit indicates the max number of returned items in the collection
 * @property {number} offset indicates the index offset for the returned queried items
 */
/**
 * @ngdoc directive
 * @name gazelle.components.filterBox.directive:gzFilterBox
 *
 * @param {string} searchText scope property for storing the search text
 * @param {searchRequest=} searchRequest callback for submitting a search request, debounced by the filter box to reduce service calls.
 * @param {Promise<listCounts>|listCounts=} listCounts promise that resolves to an object (or is just the object)
 *   containing list counts to determine if the complete data set is on the client. This object must contain the properties
 *   `total` for the total number of items in the collection and `limit` for the max amount that was queried.
 * @param {placeholder} override placeholder text (expects localized string)
 *
 * @description
 * `<gz-filter-box>` provides a styled text input element for filtering data. It includes a magnifying glass icon on the
 * far right of the input element when no text has been entered. Once text is entered the magnifying glass is replaced
 * with a X icon to provide a clickable means to clear the input.
 *
 * The filter box also allows for a hybrid behavior where data may be filtered in the client with an angular `filter`
 * or with a server query. If the `listCounts` promise is provided that resolves to the typical PrintOS list metadata,
 * then the filter box can determine if the entire data set is available to the client. If so, the filter box will set
 * the `searchText` reference. If the entire data set is not on the client, the filter box will invoke the `searchRequest`
 * callback (if provided) in a debounced manner when user input is received.
 *
 * @example
 * ````html
 * <gz-filter-box
 *  search-text='vm.userFilterText'
 *  search-request='vm.queryFromServer'
 *  list-counts='vm.initialQueryPromise'
 *  placeholder='New placeholder text'>
 * </gz-filter-box>
 * ````
 */
export const name = 'gzFilterBox';
export const SEARCH_REQUEST_DEBOUNCE_MILLIS = 1000; // exported for testing
export default function gzFilterBox() {
  //noinspection BadExpressionStatementJS
  'ngInject';

  let directive = {
    restrict: 'E',
    scope: {
      searchText: '=?',
      searchRequest: '=?',
      listCounts: '=?',
      placeholder: '@',
      changedCallback: '=?',
      enterCallback: '=?',
      filters: '=?',
      pairingId: '@'
    },
    template: require('./filter-box.jade'),
    controllerAs: 'vm',
    bindToController: true,
    controller: gzFilterBoxController
  };

  return directive;

  /////////////////

  // @ngInject
  function gzFilterBoxController(
    $log,
    $q,
    $translate,
    $scope,
    $rootScope,
    $gzAnalytics
  ) {
    const FILTER_TYPE = 'gzQuickFilter';
    let vm = this;

    vm.hasInput = false;

    vm.keyupHandler = keyupHandler;
    vm.keydownHandler = keydownHandler;
    vm.keypressHandler = keypressHandler;
    vm.clearInput = clearInput;
    vm.inputText = inputText;

    let hasAllData = true;
    let _inputText = '';
    let debouncedSearchRequest;
    let filterRemoveListener;
    let filterRemoveAllListener;
    let quickFilterChangedListener;

    vm.$onInit = onInit;
    return;

    //////////////////
    function onInit() {
      if (vm.filters) {
        let quickFilter = _.get(vm.filters, 'quickFilter');
        vm.filters = {};
        if (quickFilter !== undefined) {
          vm.filters.quickFilter = quickFilter;
          vm.searchText = quickFilter.value;
        }
      }
      else {
        vm.filters = {};
      }

      filterRemoveListener = $rootScope.$on('gzFilterRemovedEvent', (event, data) => {
        if (data.filterId === FILTER_TYPE && vm.pairingId === data.pairingId) {
          clearInput();
        }
      });

      filterRemoveAllListener = $rootScope.$on('gzFilterRemoveAllEvent', (event, data) => {
        if (vm.pairingId === data.pairingId) {
          clearInput();
        }
      });

      quickFilterChangedListener = $rootScope.$on('gzQuickFilterChangedEvent', (event, data) => {
        if (vm.pairingId === data.pairingId && data.force) {
          inputText(data.quickFilter.value);
          vm.hasInput = true;
        }
      });

      $scope.$on('$destroy', () => {
        filterRemoveListener();
        filterRemoveAllListener();
        quickFilterChangedListener();
      });

      if (!vm.placeholder) {
        $translate('uxf.uxf.QuickFilter_Placeholder').then(text => {
          vm.placeholder = text;
        });
      }


      if (vm.searchText !== undefined && vm.searchText !== null && vm.searchText !== '') {
        inputText(vm.searchText);
        vm.hasInput = true;
      }

      $scope.$watch('vm.searchText', (newValue, oldValue) => {
        if (!newValue) {
          newValue = '';
        }

        if (_inputText !== newValue) {
          inputText(newValue);
        }

        vm.hasInput = _inputText.length > 0;
      });

      setHasAllData();
      if(vm.searchRequest !== undefined) {
        debouncedSearchRequest = _.debounce(vm.searchRequest, SEARCH_REQUEST_DEBOUNCE_MILLIS);
      }
    }

    function setHasAllData() {
      if(vm.listCounts === undefined) {
        return;
      }

      $q.when(vm.listCounts).then(counts => {
        if(counts.total === undefined) {
          $log.warn('gzFilterBox expects listCounts with a total', counts);
        }
        if(counts.limit === undefined) {
          $log.warn('gzFilterBox expects listCounts with a limit', counts);
        }

        hasAllData = (counts.total <= counts.limit);
      });
    }

    function keydownHandler($event) {
      $event.stopPropagation();
    }

    function keyupHandler($event) {
      $event.stopPropagation();
      if ($event.keyCode === 13 && vm.enterCallback) {
        vm.enterCallback($event);
      }

      if ($event.keyCode === 27) {
        clearInput();
      }
      else {
        vm.hasInput = _inputText.length > 0;
      }
    }

    function keypressHandler($event) {
      $event.stopPropagation();
    }

    function clearInput($event) {
      if ($event) {
        $event.stopPropagation();
      }

      _inputText = '';
      vm.hasInput = false;
      handleInputChanged(_inputText, true);
    }

    // Use a combo getterSetter method for intercepting when the input changes.
    function inputText(updatedText) {
      if(arguments.length > 0) {
        _inputText = updatedText;
        handleInputChanged(updatedText);
      }
      return _inputText;
    }

    function handleInputChanged(updatedText, immediate = false) {
      if(hasAllData) {
        vm.searchText = updatedText;
      } else if(debouncedSearchRequest) {
        vm.searchText = '';

        if(immediate) {
          // Cancel any pending debounced calls and immediately invoke an update
          debouncedSearchRequest.cancel();
          vm.searchRequest(updatedText);
        }
        else {
          debouncedSearchRequest(updatedText);
        }
      }

      handleChangeCallback(updatedText);
    }

    function handleChangeCallback(searchText) {
      if (searchText === '') {
        if (vm.filters) {
          delete vm.filters.quickFilter;
        }
      }
      else {
        let quick = {
          id: 'gzQuickFilter',
          value: searchText
        };
        _.set(vm.filters, 'quickFilter', quick);
      }

      $rootScope.$emit('gzQuickFilterChangedEvent', {
        pairingId: vm.pairingId,
        quickFilter: vm.filters.quickFilter
      });

      if (searchText && searchText !== '') {
        $gzAnalytics.event('Quick Filter (' + vm.pairingId + ')', {value: searchText});
      }

      if (vm.changedCallback) {
        vm.changedCallback(searchText);
      }
    }
  }
}
